/**
 * Editor riutilizzabile per la lista degli indirizzi di consegna alternativi
 * di un cliente. Usato in admin/agente/magazzino quando si crea o modifica un cliente.
 *
 * Props:
 *  - value: array di indirizzi { id, label, address, cap, city, province, notes }
 *  - onChange: (nuovo array) => void
 *
 * Ogni indirizzo ha un id generato lato client (uuid v4 lite) per usarlo come key React stabile.
 */
import React from "react";
import { Plus, Trash2, MapPin } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Card } from "./ui/card";

const uid = () => {
  // RFC4122 v4 light — sufficient for client-side keys
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === "x" ? r : (r & 0x3 | 0x8)).toString(16);
  });
};

const EMPTY = () => ({ id: uid(), label: "", address: "", cap: "", city: "", province: "", notes: "" });

export const DeliveryAddressesEditor = ({ value = [], onChange, dataTestidPrefix = "delivery-addr" }) => {
  // Assicura che ogni indirizzo abbia un id stabile (alcuni vecchi record nel DB non l'hanno)
  const items = (value || []).map((a) => (a.id ? a : { ...a, id: uid() }));

  const update = (id, patch) => {
    onChange(items.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  };
  const remove = (id) => onChange(items.filter((a) => a.id !== id));
  const add = () => onChange([...items, EMPTY()]);

  return (
    <div className="space-y-3" data-testid={`${dataTestidPrefix}-editor`}>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold flex items-center gap-1">
          <MapPin className="w-4 h-4 text-[#8A1C2A]" /> Indirizzi di consegna alternativi
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={add}
          className="border-[#C5A059] text-[#8A1C2A]"
          data-testid={`${dataTestidPrefix}-add-btn`}
        >
          <Plus className="w-3 h-3 mr-1" /> Aggiungi indirizzo
        </Button>
      </div>

      {items.length === 0 && (
        <p className="text-xs text-stone-500 italic">Nessun indirizzo alternativo. Le consegne andranno alla ragione sociale.</p>
      )}

      {items.map((addr, idx) => (
        <Card key={addr.id} className="p-3 bg-stone-50 border-stone-200" data-testid={`${dataTestidPrefix}-row-${idx}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-stone-600 uppercase">Indirizzo #{idx + 1}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => remove(addr.id)}
              className="text-red-600 hover:bg-red-50 h-7 px-2"
              data-testid={`${dataTestidPrefix}-remove-${idx}`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Etichetta</Label>
              <Input
                value={addr.label || ""}
                onChange={(e) => update(addr.id, { label: e.target.value })}
                placeholder="Es. Filiale Roma, Magazzino, ecc."
                className="h-9 bg-white"
                data-testid={`${dataTestidPrefix}-label-${idx}`}
              />
            </div>
            <div>
              <Label className="text-xs">Indirizzo</Label>
              <Input
                value={addr.address || ""}
                onChange={(e) => update(addr.id, { address: e.target.value })}
                placeholder="Via, Numero civico"
                className="h-9 bg-white"
                data-testid={`${dataTestidPrefix}-address-${idx}`}
              />
            </div>
            <div className="grid grid-cols-3 gap-2 md:col-span-2">
              <div>
                <Label className="text-xs">CAP</Label>
                <Input
                  value={addr.cap || ""}
                  onChange={(e) => update(addr.id, { cap: e.target.value })}
                  placeholder="20100"
                  className="h-9 bg-white"
                  data-testid={`${dataTestidPrefix}-cap-${idx}`}
                />
              </div>
              <div>
                <Label className="text-xs">Città</Label>
                <Input
                  value={addr.city || ""}
                  onChange={(e) => update(addr.id, { city: e.target.value })}
                  placeholder="Milano"
                  className="h-9 bg-white"
                  data-testid={`${dataTestidPrefix}-city-${idx}`}
                />
              </div>
              <div>
                <Label className="text-xs">Prov.</Label>
                <Input
                  value={addr.province || ""}
                  onChange={(e) => update(addr.id, { province: e.target.value })}
                  placeholder="MI"
                  className="h-9 bg-white"
                  maxLength={2}
                  data-testid={`${dataTestidPrefix}-prov-${idx}`}
                />
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Note di consegna</Label>
              <Input
                value={addr.notes || ""}
                onChange={(e) => update(addr.id, { notes: e.target.value })}
                placeholder="Es. Consegnare dopo le 14, citofono Mario, ecc."
                className="h-9 bg-white"
                data-testid={`${dataTestidPrefix}-notes-${idx}`}
              />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

/**
 * Helper: trasforma un delivery_address in stringa formattata per il dropdown ordine
 * o per i PDF (DDT, conferma).
 */
export const formatDeliveryAddress = (a) => {
  if (!a) return "";
  const head = a.label ? `${a.label} — ` : "";
  const street = a.address || "";
  const loc = [a.cap, a.city].filter(Boolean).join(" ");
  const prov = a.province ? ` (${a.province})` : "";
  return `${head}${street}${loc ? ", " + loc : ""}${prov}`;
};

export default DeliveryAddressesEditor;
